// Copyright 2025 Imprivata, Inc. All rights reserved.

import type { ImportResult } from '@api/types';
import { Button } from '@imprivata-cloud/components';
import { useTranslation } from 'react-i18next';
import { FILES_DOWNLOAD_NAMES } from '@/portal/constants';
import { downloadAsFileToUserComputer } from '@/utils/utils';
import ErrorImg from '../../../../../assets/icons/error.svg?react';
import SuccessImg from '../../../../../assets/icons/success.svg?react';

import './ImportUsersModal.less';

const TRANSLATION_ROOT = 'portal.settings.identity-users.import-users-modal.results';

interface ImportUsersResultProps {
  importResult?: ImportResult;
  errorReport?: Blob;
  onDone: () => void;
}

const ImportUsersResult = ({ importResult, errorReport, onDone }: ImportUsersResultProps) => {
  const { t } = useTranslation();
  const successfulRows = importResult?.successfulRows ?? 0;
  const failedRows = importResult?.failedRows ?? 0;

  // Download csv error report
  const handleDownloadErrors = () => {
    if (!errorReport) {
      return;
    }
    downloadAsFileToUserComputer(errorReport, FILES_DOWNLOAD_NAMES.USERS_IMPORT_ERROR_REPORT);
  };

  return (
    <div className="results">
      <h2 className="title">{t(`${TRANSLATION_ROOT}.title`)}</h2>

      {successfulRows > 0 && (
        <>
          <div className="row row-success">
            <SuccessImg />
            <span className="count">{successfulRows}</span>
            <span className="text">{t(`${TRANSLATION_ROOT}.success-count`)}</span>
          </div>
          <div className="spacer" />
        </>
      )}

      {failedRows > 0 && (
        <div className="row row-error">
          <ErrorImg />
          <span className="count">{failedRows}</span>
          <span className="text">{t(`${TRANSLATION_ROOT}.error-count`)}</span>
        </div>
      )}

      <div className="modal-footer">
        <Button
          aria-label="button-import"
          label={t(`${TRANSLATION_ROOT}.download-errors`)}
          disabled={failedRows === 0 || !errorReport}
          size="small"
          type="primary"
          onClick={handleDownloadErrors}
        />
        <Button
          aria-label="button-done"
          size="small"
          label={t(`${TRANSLATION_ROOT}.done`)}
          onClick={onDone}
        />
      </div>
    </div>
  );
};

export default ImportUsersResult;
